import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { CourseItem, CourseResponse } from "../../interfaces/courses";
import { getToken } from "../../utils/tokenStore";

const initialState = {
  loading: false,
  courses: [] as CourseItem[],
  courseResp: {} as CourseResponse,
};

interface QueryProps {
  page_size?: number;
  page?: number;
  sort_attr?: string;
  sort_order?: "asc" | "desc";
  filter_value?: string;
  role?: string;
  hr_tab?: string;
  user_id?: string | null;
  additional_filter_name?: any;
  additional_filter_value?: any;
  additional_filter_operator?: any;
}

export const getCourses = createAsyncThunk(
  "courses/getCourses",
  async (props: QueryProps, { getState, rejectWithValue }) => {
    try {
      let query = "?";

      for (const [key, value] of Object.entries(props)) {
        query += `${key}=${value}&`;
      }

      const url = `${process.env.REACT_APP_BASE_API_URL}/courses${query}`;

      const token = getToken();
      const response = await fetch(url, {
        method: "GET",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }).then((res) => res.json());

      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Authorization failed"
      );
    }
  }
);

export const updateCourseCost = createAsyncThunk(
  "courses/updateCourseCost",
  async (data: any, { getState, rejectWithValue }) => {
    try {
      const { courseId, updatedCosts } = data;
      const token = getToken();
      const domainName = window.location.hostname;
      const _url =
        domainName === "prod.orbico.toa.apprenderesaas.eu"
          ? "https://www.orbicollege.com"
          : "https://devorbicollege.orbico.com";
      const url = `${_url}/course/v1/courses/${courseId}/additionalfields/batch`;

      const response = await fetch(url, {
        method: "POST",
        body: JSON.stringify(updatedCosts),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }).then((res) => res.json());

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Authorization failed"
      );
    }
  }
);

// Async thunk for fetching additional fields for a course
export const fetchAdditionalFields = createAsyncThunk(
  "course/fetchAdditionalFields",
  async (courseId: any, { getState, rejectWithValue }) => {
    try {
      const token = getToken();
      const domainName = window.location.hostname;
      const _url =
        domainName === "prod.orbico.toa.apprenderesaas.eu"
          ? "https://www.orbicollege.com"
          : "https://devorbicollege.orbico.com";
      const url = `${_url}/course/v1/courses/${courseId}/additionalfields`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }).then((res) => res.json());

      return { courseId, items: response.data.items };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Authorization failed"
      );
    }
  }
);

const coursesSlice = createSlice({
  name: "courses",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getCourses.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCourses.fulfilled, (state, action) => {
        state.loading = false;
        state.courses = action.payload?.data || [];
        state.courseResp = action.payload;
      })
      .addCase(getCourses.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(updateCourseCost.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCourseCost.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(updateCourseCost.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(fetchAdditionalFields.pending, (state) => {
        // state.loading = true;
      })
      .addCase(fetchAdditionalFields.fulfilled, (state, action) => {
        const { courseId } = action.payload;
        const fields = action.payload.items;
        const course: any = state.courses.find(
          (course) => course.course_id === courseId
        );
        if (course) {
          const onlineField = fields.find(
            (field: any) =>
              field.title === "Online training cost (OC)" &&
              field.id === course.online_training_cost_id
          );
          const classroomField = fields.find(
            (field: any) =>
              field.title === "Classroom training cost (OC)" &&
              field.id === course.classroom_training_cost_id
          );
          if (onlineField && classroomField) {
            course.online_training_cost = onlineField.value || "NA";

            course.classroom_training_cost = classroomField.value || "NA";
          }
        }

        state.loading = false;
      })
      .addCase(fetchAdditionalFields.rejected, (state, action) => {
        state.loading = false;
      });
  },
});

export default coursesSlice.reducer;
